<template>
    <EditableRoot
        ref="root"
        :model-value="modelValue"
        :placeholder="placeholder ?? ''"
        :disabled="disabled"
        :max-length="maxLength"
        :select-on-focus="selectOnFocus"
        activation-mode="none"
        :submit-mode="rekaSubmitMode"
        class="base-editable-field"
        :class="rootClass()"
        :style="rootStyle()"
        :data-testid="testId()"
        @update:model-value="handleRekaUpdate"
        @submit="handleRekaSubmit"
        @update:state="handleRekaState">
        <span v-if="$slots.prefix" class="affix">
            <slot name="prefix"></slot>
        </span>
        <!-- reka hides whichever of the preview and the control is not in use. The frame only
             draws around the control while editing, so at rest the value reads as plain text. -->
        <EditableArea class="area">
            <EditablePreview ref="preview" class="preview" tabindex="-1" :data-testid="partTestId('preview')">
                <slot name="display" :value="modelValue">{{ modelValue || placeholder }}</slot>
            </EditablePreview>
            <!-- Named by the caller's label prop, or by a wrapping FormField's label through
                 aria-labelledby. Caller attrs (rows, autocomplete, listeners, ...) reach the control. -->
            <EditableInput
                ref="control"
                :as="multiline ? 'textarea' : 'input'"
                v-bind="controlAttrs()"
                class="control fs-mask"
                :data-testid="partTestId('input')"
                @input="handleInput"
                @keydown="handleKeydown" />
        </EditableArea>
        <BaseButton
            v-if="!editing && !hideEditButton"
            ref="editButton"
            theme="tertiary"
            size="sm"
            icon-only
            :disabled="disabled"
            :aria-label="t('components.editableField.edit', { label })"
            :data-testid="partTestId('edit')"
            @click="edit">
            <i class="bi bi-pencil" aria-hidden="true"></i>
        </BaseButton>
        <template v-else-if="editing">
            <BaseButton
                theme="secondary"
                size="sm"
                icon-only
                :disabled="!canSubmit"
                :aria-label="t('components.editableField.save', { label })"
                :data-testid="partTestId('submit')"
                @click="submit">
                <i class="bi bi-check text-lg/none" aria-hidden="true"></i>
            </BaseButton>
            <BaseButton theme="tertiary" size="sm" icon-only :aria-label="t('components.editableField.cancel', { label })" :data-testid="partTestId('cancel')" @click="cancel">
                <i class="bi bi-x text-lg/none" aria-hidden="true"></i>
            </BaseButton>
        </template>
    </EditableRoot>
</template>

<script lang="ts">
interface BaseEditableFieldProps {
    /**
     * Accessible name of the control and the stem of the edit, save and cancel button names
     * ("Edit name"). Inside a FormField the field's label names the control instead and
     * this still names the buttons. A visible label is the FormField's job.
     */
    label: string;
    /** Shown in placeholder color while the value is empty, and inside the control while editing. */
    placeholder?: string;
    disabled?: boolean;
    maxLength?: number;
    /**
     * `enter` saves on Enter or the save button and discards the draft when focus or the pointer
     * leaves the field; `blur` saves when they leave too. Escape always discards. An Enter with
     * nothing to save only ends editing and is not prevented, so inside a form it submits the form.
     * In multi-line mode Enter inserts a line break, so `enter` means the save button only.
     */
    submitMode?: "enter" | "blur";
    /** `compact` tightens the edit frame for dense rows and headers. */
    size?: "default" | "compact";
    /** Renders a textarea instead of an input. Pass `rows` like any other textarea attribute. */
    multiline?: boolean;
    /**
     * An empty value cannot be saved: the save button is disabled, Enter keeps the field open,
     * and leaving the field in `blur` mode discards the draft instead of saving it. The control
     * carries aria-required so screen readers announce it.
     */
    required?: boolean;
    /** Selects the current value when editing starts. */
    selectOnFocus?: boolean;
    /** Hides the pencil for callers that start editing elsewhere (a menu action) through the exposed `edit()`. */
    hideEditButton?: boolean;
    /**
     * Emits update:modelValue on every keystroke instead of only on save, for a field that is part of
     * a larger form whose submit button follows the typed value. Cancel puts back the value editing
     * started from. Enter ends editing and, inside a form, submits it.
     */
    live?: boolean;
    /**
     * The saved value. The field never keeps a copy: a save emits update:modelValue and submit, and
     * the text shown at rest follows this prop, so a save the owner rejects or has not persisted
     * yet does not show as done.
     */
    modelValue?: string;
}
</script>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { isImeComposing } from "~vue/utils/keyboard";
import { EditableArea, EditableInput, EditablePreview, EditableRoot } from "reka-ui";
import { type ComponentPublicInstance, computed, type HTMLAttributes, nextTick, ref, type StyleValue, useAttrs, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { useFormField } from "./formFieldContext";

defineOptions({ inheritAttrs: false });

defineSlots<{
    /** Leading content outside the edit frame, such as an icon. Mark icons aria-hidden. */
    prefix?(): unknown;
    /** Replaces the plain text shown at rest, for a truncated or differently styled value. */
    display?(props: { value: string }): unknown;
}>();

const {
    modelValue = "",
    label,
    placeholder,
    disabled = false,
    maxLength,
    submitMode = "enter",
    size = "default",
    multiline = false,
    required = false,
    selectOnFocus = false,
    hideEditButton = false,
    live = false,
} = defineProps<BaseEditableFieldProps>();

const emit = defineEmits<{
    "update:modelValue": [value: string];
    /** The draft was saved, after update:modelValue carried the same value. */
    submit: [value: string];
    /** Editing ended without saving: Escape, the cancel button, or leaving the field in `enter` mode. */
    cancel: [];
}>();

const { t } = useI18n();

// Wraps itself into a surrounding FormField when present; used bare, the label prop names the control.
const field = useFormField();

// Read in template helpers so every render sees the current attrs (BaseInput does the same).
const attrs = useAttrs();

const root = useTemplateRef<InstanceType<typeof EditableRoot>>("root");
const control = useTemplateRef<ComponentPublicInstance>("control");
const preview = useTemplateRef<ComponentPublicInstance>("preview");
const editButton = useTemplateRef<ComponentPublicInstance>("editButton");

// reka would submit on Enter itself, which cannot honor `required` or multi-line input, so Enter
// stays in handleKeydown below and only what happens on leaving the field is delegated.
const rekaSubmitMode = computed(() => (submitMode === "blur" ? "blur" : "none"));

// Mirrors reka's editing state, which it reports through update:state and does not expose as a prop.
const editing = ref(false);
// The text typed so far, so the save button can follow `required` before anything is submitted.
const draft = ref(modelValue);
// The value editing started from, which a cancel in `live` mode puts back.
const startValue = ref(modelValue);
const canSubmit = computed(() => !(required && draft.value.trim() === ""));

function rejects(value: string): boolean {
    return required && value.trim() === "";
}

function handleInput(event: Event) {
    draft.value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    if (live) {
        emit("update:modelValue", draft.value);
    }
}

// Enter saves. When the save would change nothing (always the case in `live` mode, where the model
// already holds the draft) it only ends editing and is left alone, so inside a form it submits the
// form as it would from any other input.
function handleKeydown(event: KeyboardEvent) {
    if (multiline || event.key !== "Enter" || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey || isImeComposing(event)) {
        return;
    }
    const changesNothing = live || draft.value === modelValue;
    if (!changesNothing) {
        event.preventDefault();
    }
    submit();
}

// reka sets its model and emits submit on every save, including one it made on leaving the
// field, so a rejected empty draft is turned into a cancel here rather than reaching the model.
function handleRekaUpdate(value: string | null | undefined) {
    if (value === null || value === undefined || rejects(value)) {
        return;
    }
    emit("update:modelValue", value);
}

function handleRekaSubmit(submitted: string | null | undefined) {
    const value = submitted ?? "";
    if (rejects(value)) {
        // A live field already sent the empty draft to the model, so the rejected edit puts back its start.
        if (live && modelValue !== startValue.value) {
            emit("update:modelValue", startValue.value);
        }
        emit("cancel");
        return;
    }
    emit("submit", value);
}

function handleRekaState(state: "edit" | "submit" | "cancel") {
    editing.value = state === "edit";
    if (state === "edit") {
        draft.value = modelValue;
        startValue.value = modelValue;
        return;
    }
    if (state === "cancel") {
        if (live && modelValue !== startValue.value) {
            emit("update:modelValue", startValue.value);
        }
        emit("cancel");
    }
    restoreFocus();
}

function elementOf(instance: { $el?: unknown } | null): HTMLElement | undefined {
    return instance?.$el instanceof HTMLElement ? instance.$el : undefined;
}

// reka hides the control when editing ends, which drops focus to the page. Unless focus already moved
// somewhere else (leaving the field), it goes to the pencil, or to the value itself when there is none.
function restoreFocus() {
    const rootElement = elementOf(root.value);
    const active = document.activeElement;
    if (active && active !== document.body && !rootElement?.contains(active)) {
        return;
    }
    nextTick(() => {
        const current = document.activeElement;
        if (current && current !== document.body && !rootElement?.contains(current)) {
            return;
        }
        const target = elementOf(editButton.value) ?? elementOf(preview.value);
        target?.focus({ preventScroll: true });
    });
}

function focusControl() {
    const element = control.value?.$el as HTMLInputElement | HTMLTextAreaElement | undefined;
    element?.focus({ preventScroll: true });
    if (selectOnFocus) {
        element?.select();
    }
}

/** Starts editing and focuses the control. Called while already editing, it only refocuses and keeps the draft. */
function edit() {
    if (disabled) {
        return;
    }
    if (!editing.value) {
        root.value?.edit();
    }
    nextTick(focusControl);
}

/** Saves the draft, unless `required` rejects it. */
function submit() {
    if (!canSubmit.value) {
        return;
    }
    root.value?.submit();
}

/** Discards the draft and leaves edit mode. */
function cancel() {
    root.value?.cancel();
}

defineExpose({ edit, submit, cancel });

// A caller's class, style and data-testid dress the root; every other attr reaches the control.
// Parts derive their test ids from the root's ("profile-name" gives "profile-name-input"), so
// end-to-end tests can reach the preview, control and buttons without reaching into the markup.
function testId(): string | undefined {
    return attrs["data-testid"] as string | undefined;
}

function partTestId(part: "preview" | "input" | "edit" | "submit" | "cancel"): string | undefined {
    const id = testId();
    return id ? `${id}-${part}` : undefined;
}

// Caller attrs win over the FormField wiring, so an explicit aria-describedby still applies.
function controlAttrs(): Record<string, unknown> {
    const { class: _class, style: _style, "data-testid": _testId, ...rest } = attrs;
    // reka names the control "editable input" unless given a label, so the label prop always
    // replaces that; inside a FormField, aria-labelledby points at the field's label and outranks it.
    const naming = field ? { ...field.controlAttrs.value, "aria-labelledby": field.labelId } : {};
    // aria-required rather than the native attribute, so the browser's own validation stays out of a
    // surrounding form; the save button already refuses an empty draft.
    const requirement = required ? { "aria-required": "true" } : {};
    return { "aria-label": label, ...requirement, ...naming, ...rest };
}

function rootClass(): HTMLAttributes["class"] {
    const isInvalid = String(controlAttrs()["aria-invalid"]) === "true";
    return [
        sizeClasses[size],
        { "is-invalid": isInvalid, "is-disabled": disabled, "is-multiline": multiline, "is-editing": editing.value },
        attrs.class as HTMLAttributes["class"],
    ];
}

function rootStyle(): StyleValue {
    return attrs.style as StyleValue;
}

const sizeClasses: Record<NonNullable<BaseEditableFieldProps["size"]>, string> = {
    default: "",
    compact: "is-compact",
};
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .base-editable-field {
        @apply inline-flex min-w-0 max-w-full items-center gap-2;
    }

    .base-editable-field.is-multiline {
        @apply flex w-full items-start;
    }

    .base-editable-field .affix {
        @apply flex shrink-0 items-center text-base/none text-control-text;
    }

    .base-editable-field .area {
        @apply min-w-0 flex-1;
    }

    /* The frame draws the border and focus ring only while editing; the control inside is borderless. */
    .base-editable-field .area[data-focused] {
        @apply flex items-center rounded-md border border-control-border bg-control-bg px-2.5 py-2 transition-colors focus-field-within;
    }

    .base-editable-field.is-compact .area[data-focused] {
        @apply px-2 py-1;
    }

    .base-editable-field.is-invalid .area[data-focused] {
        @apply border-status-error;
    }

    .base-editable-field.is-disabled .area[data-focused] {
        @apply bg-control-disabled-bg;
    }

    .base-editable-field .preview {
        @apply block min-w-0 body-small text-ink wrap-anywhere;
    }

    .base-editable-field.is-multiline .preview {
        @apply whitespace-pre-line;
    }

    .base-editable-field .area[data-empty] .preview {
        @apply text-control-placeholder;
    }

    .base-editable-field .control {
        @apply w-full min-w-0 border-none bg-transparent p-0 outline-none body-small text-control-text-strong placeholder:text-control-placeholder;
    }

    .base-editable-field.is-multiline .control {
        @apply resize-none wrap-anywhere;
    }
}
</style>
